import cs from 'classnames';
import { parseAndFormatCurrency } from 'shared/utils/currency';
import { App_PortalFragment, CartItemFragment, Cart_ApplicationFragment, Condition } from '../../generated.graphql';
import { useItem } from './hooks';
import { CartLevelMinMonthsToggleButtons, ItemLevelMinMonthsToggleButtons, MinRentalPeriod } from './MinMonthsSelect';
import { NewProductDescriptionButton } from './NewProductDescription';
import { PrelovedConditionReportButton } from './PrelovedConditionReport';
import { ProtectionPlanBenifits } from './ProtectionPlanBenefits';
import { ProtectionPlanCheckbox } from './ProtectionPlanCheckbox';
import { ProtectionPlanLogo } from './ProtectionPlanLogo';
import { PurchasePricingPopoverButton } from './PurchasePricing';
import { RemoveItemBtn } from './RemoveItemBtn';
import { SuggestedItems } from './SuggestedItems';
import { UnderstoodMinPeriods, UnderstoodMinPeriodsCheckbox, UnderstoodMinPeriodsDesc } from './UnderstandMinRental';
import { isProtectionPlanAvailable } from './ProtectionPlan';


interface Props {
  application: Cart_ApplicationFragment;
  portal: App_PortalFragment;
  className?: string;
  remove: boolean
  understoodMinRental: UnderstoodMinPeriods;
}

export const CartStack = ({application, ...props}: Props) => {
  const { pricing } = application;
  return (
    <div className={props.className}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto'
      }}>
        <div className="font-weight-normal border-bottom py-2 mb-2">Items to be Rented</div>
        <div className="font-weight-normal border-bottom py-2 mb-2">Monthly Payment</div>
        
        {application.items && application.items.map(item => 
          <CartStackItem
            key={item.id}
            item={item}
            remove={props.remove}
            readonly={!!application.inStore}
            highlightMinMonths={application.portal.requireMinMonthsConfirmation}
            validateMinMonths={props.understoodMinRental.validated}
            portalSlug={application.portal.slug} />
        )}
        {application.rateOptions.length > 1 &&
          <div className="border-bottom mb-2 pb-3" style={{gridColumn: 'span 2'}}>
            <div className={cs('mb-2', props.understoodMinRental.validated && application.minMonths == null && 'text-danger')}><strong >Choose your minimum rental period</strong></div>
            <CartLevelMinMonthsToggleButtons application={application} validate={props.understoodMinRental.validated} />
          </div>
        }

        <div className="font-weight-normal mb-2">Regular monthly payments</div>
        <div className="text-right font-weight-normal mb-2">{parseAndFormatCurrency(pricing.ongoingPayment)}</div>
        
        <div className="font-weight-normal mb-3">Once off {application.importantInfo.freightHeading.toLowerCase()}</div>
        <div className="text-right font-weight-normal mb-3">{parseAndFormatCurrency(pricing.setupFee)}</div>
      </div>
      {application.suggestedItems && application.suggestedItems.length > 0 && (
        <div className="border-top mt-0 pt-3">
          <SuggestedItems items={application.suggestedItems} portalSlug={application.portal.slug} />
        </div>
      )}
      {isProtectionPlanAvailable(pricing.protectionPlan) && 
        <div className="border shadow p-3 mt-5">
          <div className='d-flex mb-3'>
            <ProtectionPlanLogo className='mr-4 py-1'/>
            <ProtectionPlanCheckbox application={application} mode='switch' className='ml-auto'/>
          </div>
          <ProtectionPlanBenifits application={application} portalSlug={props.portal.slug}/>
        </div>
      }
      {application.portal.requireMinMonthsConfirmation &&
        <div className="border shadow p-3 mt-5">
          <div className="mb-3"><UnderstoodMinPeriodsDesc {...props.understoodMinRental}/></div>
          <div className=""><UnderstoodMinPeriodsCheckbox {...props.understoodMinRental}/></div>
        </div>
      }
    </div>
  )
};


const CartStackItem = ({item, remove, readonly, highlightMinMonths, validateMinMonths, portalSlug}: {
  item: CartItemFragment,
  remove: boolean,
  readonly: boolean,
  highlightMinMonths: boolean,
  validateMinMonths: boolean,
  portalSlug: string
}) => {
  const h = useItem({item, readonly});
  const hasRateOptionSelection = item.rateOptions && item.rateOptions.length > 1;
  return (
    <>
      {/*<div className={cs("bg-white shadow", styles.ItemImageWidth)}>
        {item.imageUrl && <img src={item.imageUrl} className="img-fluid" alt="Item thumbnail"/>}
</div>*/}
      <div className={cs(hasRateOptionSelection ? "mb-1" : "mb-3 pb-3 border-bottom")}>
        {/*<div className="font-weight-normal">{h.formattedCondition}</div>*/}
        <div className="mb-1 font-weight-normal">{item.condition === Condition.Preloved ? h.formattedCondition : ''} {h.formattedName}</div>
        <div className="mb-1">{item.subtitle}</div>
        <div className="small mb-2">
          {!hasRateOptionSelection &&
            <MinRentalPeriod minMonths={item.selectedRate.minMonths ?? item.minMonths} highlight={highlightMinMonths}/>
          }
        </div>

        <div><PurchasePricingPopoverButton rate={item.selectedRate} startValue={item.startValue} className="btn btn-link px-0 py-0 btn-sm font-weight-light" caret={true} /></div>
        {item.newProductDescription && 
          <div><NewProductDescriptionButton item={item} className="btn btn-link px-0 py-0 btn-sm font-weight-light text-decoration-italic" caret={true} /></div>
        }
        {item.prelovedConditionReport &&
          <div><PrelovedConditionReportButton item={item} className="btn btn-link px-0 py-0 btn-sm font-weight-light" caret={true} /></div>
        }
      </div>
      <div className={cs(hasRateOptionSelection ? "mb-1" : "mb-3 pb-3 border-bottom", "text-right pl-2")}>
        <div className="font-weight-normal">
          {h.formattedRate}
          {remove && (
            <RemoveItemBtn 
              disabled={h.isRemoving}
              onRemove={async () => {await h.remove()}}
              className="btn btn-link px-2"
            />
          )}
        </div>
        <h.QtySelect className="form-control custom-select">
          {item.qty > 1 ? (<div className="small mr-2">Quantity: {item.qty}</div>) : (<></>)}
        </h.QtySelect>
      </div>
      {hasRateOptionSelection &&
        <div className='mb-3 pb-3 border-bottom small' style={{ gridColumn: '1 / 3'}}>
          <div className={cs('mb-1', validateMinMonths && !item.minMonthsConfirmed && 'text-danger')}>Choose your minimum rental period</div>
          <ItemLevelMinMonthsToggleButtons className="w-100" portalSlug={portalSlug} itemId={item.id} rateOptions={item.rateOptions!} value={item.minMonthsConfirmed ? item.minMonths : null} validate={validateMinMonths} />
        </div>
      }
    </>
  )
};
